import type { ApiQuestion, ApiQuestionSetting } from '../../../../internalTypes';
import { QUESTION_CONFIG_KEY } from '../../../../constants/questionConfigKeys';
import type { ContainerMedia, MediaAttachment } from '../../../../types/shared';
import { shouldShowQuestionHint } from '../../../../../surveyLogic/fields/questionHintUtils';
import { getConfigFlag } from './configUtils';
import {
  mapContainerMedia,
  mapMediaAttachments,
  mapMediaFeatureFlags,
} from './mapQuestionMedia';

/** Internal mapper output — not exported to SDK clients. */
export type ResolvedQuestionShell = {
  id: string;
  questionText: string;
  questionDescription?: string;
  questionHint?: string;
  required: boolean;
  requiredErrorMessage?: string;
  questionNumber?: number;
  containerMedia?: ContainerMedia;
  mediaAttachments?: MediaAttachment[];
  enablePreview?: boolean;
  enableDownload?: boolean;
  referencedQuestionIds?: string[];
};

export function resolveQuestionShell(
  raw: ApiQuestion,
  configs: ApiQuestionSetting[],
  language?: string,
  placeholders?: Record<string, string>
): ResolvedQuestionShell {
  const required = getConfigFlag(configs, QUESTION_CONFIG_KEY.REQUIRED);

  const reqErrConfig = configs.find(c => c.key === QUESTION_CONFIG_KEY.REQUIRED_ERROR_MESSAGE);
  let requiredErrorMessage = reqErrConfig?.value?.[0];
  if (language && reqErrConfig?.translations?.[language]?.value?.[0]) {
    requiredErrorMessage = reqErrConfig.translations[language].value![0];
  }

  let questionText =
    (language && raw.translations?.[language]?.questionRichText) ||
    (language && raw.translations?.[language]?.questionText) ||
    raw.questionRichText ||
    raw.questionText ||
    '';

  if (placeholders) {
    questionText = questionText.replace(/\{\{(\w+)\}\}/g, (match, key) =>
      placeholders[key] !== undefined ? placeholders[key] : match
    );
  }

  let questionDescription =
    (language && raw.translations?.[language]?.descriptionRichText) ||
    (language && raw.translations?.[language]?.description) ||
    raw.descriptionRichText ||
    raw.description ||
    raw.descriptionText ||
    (typeof raw.additional?.descriptionRichText === 'string' ? raw.additional.descriptionRichText : '') ||
    (typeof raw.additional?.description === 'string' ? raw.additional.description : '') ||
    '';

  if (!questionDescription) {
    const descConfig = configs.find(c => c.key.includes('DESCRIPTION'));
    questionDescription = descConfig?.value?.[0] || '';
    if (language && descConfig?.translations?.[language]?.valueRichText?.[0]) {
      questionDescription = descConfig.translations[language].valueRichText![0];
    } else if (language && descConfig?.translations?.[language]?.value?.[0]) {
      questionDescription = descConfig.translations[language].value![0];
    }
  }

  if (questionDescription && placeholders) {
    questionDescription = questionDescription.replace(/\{\{(\w+)\}\}/g, (match, key) =>
      placeholders[key] !== undefined ? placeholders[key] : match
    );
  }

  let questionHint = raw.questionHint;
  const hintConfig = configs.find(c => c.key === 'QUESTION_HINT');
  if (hintConfig?.value?.[0] && hintConfig.value[0] !== 'true' && hintConfig.value[0] !== 'false') {
    questionHint = hintConfig.value[0];
  }

  const containerMedia = mapContainerMedia(raw, configs);
  const mediaAttachments = mapMediaAttachments(raw, configs);
  const mediaFlags = mapMediaFeatureFlags(configs);

  const associatedIds = raw.additionalInfo?.associatedPlaceholderQuestionIds;
  const referencedQuestionIds = Array.isArray(associatedIds)
    ? associatedIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : undefined;

  return {
    id: raw.id ?? '',
    questionText,
    ...(questionDescription ? { questionDescription } : {}),
    ...(shouldShowQuestionHint(questionHint) ? { questionHint } : {}),
    required,
    ...(requiredErrorMessage ? { requiredErrorMessage } : {}),
    ...(raw.questionNumber !== undefined ? { questionNumber: raw.questionNumber } : {}),
    ...(containerMedia ? { containerMedia } : {}),
    ...(mediaAttachments ? { mediaAttachments } : {}),
    ...mediaFlags,
    ...(referencedQuestionIds && referencedQuestionIds.length > 0
      ? { referencedQuestionIds }
      : {}),
  };
}
